import React from 'react';
import {
  Box, Typography, Drawer, IconButton, Chip, Divider, Avatar,
  CircularProgress, Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ErrorIcon from '@mui/icons-material/Error';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CancelIcon from '@mui/icons-material/Cancel';
import PauseIcon from '@mui/icons-material/Pause';
import InstagramIcon from '@mui/icons-material/Instagram';

const STATUS_COLORS = { active: '#10b981', paused: '#f59e0b', completed: '#6366f1', cancelled: '#ef4444' };

const timeAgo = (date) => {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const timeUntil = (date) => {
  const diff = new Date(date).getTime() - Date.now();
  if (diff <= 0) return 'now';
  const hrs = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  if (hrs > 24) return `${Math.floor(hrs / 24)}d ${hrs % 24}h`;
  if (hrs > 0) return `${hrs}h ${mins}m`;
  return `${mins}m`;
};

const initial = (name) => (name || '?').charAt(0).toUpperCase();
const avatarColor = (name) => {
  const colors = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

export default function CampaignDrawer({ open, campaign, loading, onClose, onAction }) {
  if (!campaign) return null;

  const steps = campaign.sequenceId?.steps || [];
  const totalSteps = steps.length;
  const statusColor = STATUS_COLORS[campaign.status] || '#6b7280';

  const getStepStatus = (index) => {
    const sent = (campaign.sentEmails || []).find(e => e.stepIndex === index);
    if (sent && sent.error) return { label: 'Failed', icon: ErrorIcon, color: '#ef4444', data: sent };
    if (sent) return { label: 'Sent', icon: CheckCircleIcon, color: '#10b981', data: sent };
    if (index === campaign.currentStep && campaign.status === 'active') return { label: 'Pending', icon: ScheduleIcon, color: '#f59e0b', data: null };
    return { label: 'Upcoming', icon: RadioButtonUncheckedIcon, color: '#d1d5db', data: null };
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, maxWidth: '100vw' } }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>
      ) : (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.5, borderBottom: '1px solid #f3f4f6' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: avatarColor(campaign.contactName), fontSize: 14, fontWeight: 700 }}>
                {initial(campaign.contactName)}
              </Avatar>
              <Box>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{campaign.contactName}</Typography>
                <Typography sx={{ fontSize: 12, color: '#6b7280' }}>{campaign.contactEmail}</Typography>
              </Box>
            </Box>
            <IconButton onClick={onClose}><CloseIcon /></IconButton>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
            {/* Status + Sequence */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <Chip label={campaign.status} size="small" sx={{
                bgcolor: `${statusColor}15`, color: statusColor, fontWeight: 700, fontSize: 11, textTransform: 'capitalize',
              }} />
              {campaign.source === 'instagram' && (
                <Chip icon={<InstagramIcon sx={{ fontSize: 13 }} />} label="Instagram" size="small" sx={{
                  bgcolor: '#fce4ec', color: '#e1306c', fontWeight: 600, fontSize: 10,
                }} />
              )}
              <Typography sx={{ fontSize: 13, color: '#6b7280' }}>{campaign.sequenceId?.name || 'Unknown'}</Typography>
            </Box>

            {/* Quick actions */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
              {campaign.status === 'active' && (
                <Button size="small" variant="outlined" startIcon={<PauseIcon />} onClick={() => onAction?.('pause')}
                  sx={{ borderRadius: 2, textTransform: 'none', fontSize: 12, borderColor: '#d1d5db', color: '#374151' }}>
                  Pause
                </Button>
              )}
              {campaign.status === 'paused' && (
                <Button size="small" variant="outlined" startIcon={<PlayArrowIcon />} onClick={() => onAction?.('resume')}
                  sx={{ borderRadius: 2, textTransform: 'none', fontSize: 12, borderColor: '#d1d5db', color: '#374151' }}>
                  Resume
                </Button>
              )}
              {['active', 'paused'].includes(campaign.status) && (
                <Button size="small" variant="outlined" startIcon={<CancelIcon />} onClick={() => onAction?.('cancel')}
                  sx={{ borderRadius: 2, textTransform: 'none', fontSize: 12, borderColor: '#fecaca', color: '#ef4444' }}>
                  Cancel
                </Button>
              )}
            </Box>

            {/* Campaign info */}
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
              Campaign Info
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3, p: 2, bgcolor: '#f9fafb', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Started</Typography>
                <Typography sx={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>
                  {campaign.startedAt ? new Date(campaign.startedAt).toLocaleString() : '—'}
                </Typography>
              </Box>
              {campaign.completedAt && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Completed</Typography>
                  <Typography sx={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>
                    {new Date(campaign.completedAt).toLocaleString()}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Total Steps</Typography>
                <Typography sx={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>{totalSteps}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Current Step</Typography>
                <Typography sx={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>{campaign.currentStep}/{totalSteps}</Typography>
              </Box>
              {campaign.status === 'active' && campaign.nextSendAt && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Next Send</Typography>
                  <Typography sx={{ fontSize: 12, color: '#f59e0b', fontWeight: 600 }}>
                    {timeUntil(campaign.nextSendAt)}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Timeline */}
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
              Timeline
            </Typography>
            <Box sx={{ position: 'relative', ml: 1 }}>
              {steps.map((step, i) => {
                const { icon: StepIcon, color, data } = getStepStatus(i);
                const isCurrent = i === campaign.currentStep && campaign.status === 'active';
                return (
                  <Box key={i} sx={{ display: 'flex', gap: 2, pb: i < totalSteps - 1 ? 2.5 : 0, position: 'relative' }}>
                    {/* Connector line */}
                    {i < totalSteps - 1 && (
                      <Box sx={{
                        position: 'absolute', left: 10, top: 24, width: 2,
                        bgcolor: data ? (data.error ? '#fecaca' : '#d1d5db') : '#e5e7eb',
                        height: `calc(100% - 4px)`,
                      }} />
                    )}
                    {/* Icon */}
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                      <StepIcon sx={{ fontSize: 20, color }} />
                    </Box>
                    {/* Content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: 13, fontWeight: isCurrent ? 700 : 500, color: data || isCurrent ? '#111827' : '#9ca3af' }}>
                          {step.subject || `Step ${i + 1}`}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>+{step.delayHours || 0}h</Typography>
                      </Box>
                      {data && (
                        <Typography sx={{ fontSize: 11, color: data.error ? '#ef4444' : '#6b7280', mt: 0.3 }}>
                          {timeAgo(data.sentAt)}
                          {data.error && <span> — Failed: {data.error}</span>}
                        </Typography>
                      )}
                      {isCurrent && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                          <Typography sx={{ fontSize: 11, color: '#f59e0b', fontWeight: 600 }}>
                            {campaign.nextSendAt ? `Sending ${timeUntil(campaign.nextSendAt)}` : 'Sending now'}
                          </Typography>
                        </Box>
                      )}
                      {data && (
                        <Box sx={{ mt: 1, p: 1.5, bgcolor: '#f9fafb', borderRadius: 1.5, border: '1px solid #f3f4f6' }}>
                          <Typography sx={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic', wordBreak: 'break-word' }}>
                            {step.body?.slice(0, 200)}{(step.body?.length || 0) > 200 ? '...' : ''}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Sent emails summary */}
            {(campaign.sentEmails?.length || 0) > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
                  Sent Emails ({campaign.sentEmails.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {campaign.sentEmails.map((e, i) => (
                    <Box key={i} sx={{ p: 1.5, bgcolor: '#f9fafb', borderRadius: 1.5, border: '1px solid #f3f4f6' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{e.subject}</Typography>
                        {e.error ? (
                          <Chip label="Failed" size="small" sx={{ bgcolor: '#fef2f2', color: '#ef4444', fontSize: 10, fontWeight: 600 }} />
                        ) : (
                          <Chip label="Sent" size="small" sx={{ bgcolor: '#f0fdf4', color: '#16a34a', fontSize: 10, fontWeight: 600 }} />
                        )}
                      </Box>
                      <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.3 }}>
                        {timeAgo(e.sentAt)}
                        {e.error && <Typography component="span" sx={{ color: '#ef4444', ml: 1 }}>Error: {e.error}</Typography>}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}
    </Drawer>
  );
}
